import React, { useState, useRef, useEffect, useCallback } from "react";
import clsx from "clsx";
import { searchABN } from "@/lib/utils";
import debounce from "lodash.debounce";

interface ABNInputProps {
	value: string;
	onChange: (value: string) => void;
	error?: boolean;
}

const ABNInput: React.FC<ABNInputProps> = ({ value, onChange, error }) => {
	const [suggestions, setSuggestions] = useState<ABNSuggestion[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const inputRef = useRef<HTMLInputElement | null>(null);
	const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

	const handleInputChange = useCallback(
		debounce(async (value: string) => {
			if (value.length < 3) {
				setSuggestions([]);
				return;
			}

			setIsLoading(true);
			try {
				const suggestions = await searchABN(value);

				// Deduplicate by ABN
				const uniqueSuggestions = Array.from(
					new Map(suggestions?.map((s) => [s.abn, s])).values()
				);

				setSuggestions(uniqueSuggestions ?? []);
			} catch (error) {
				console.error("Failed to fetch ABN suggestions:", error);
				setSuggestions([]);
			} finally {
				setIsLoading(false);
			}
		}, 300), // wait 300ms after user stops typing
		[]
	);

	const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const nextValue = event.target.value;
		onChange(nextValue);
		handleInputChange(nextValue);
	};

	const handleSuggestionClick = (abn: string) => {
		onChange(abn);
		setSuggestions([]);
	};

	useEffect(() => {
		if (inputRef.current) {
			const { offsetWidth, offsetTop } = inputRef.current;
			setDropdownStyle({
				width: `${offsetWidth}px`,
				top: `${offsetTop + 40}px`,
				left: `${inputRef.current.offsetLeft}px`,
			});
		}
	}, [value, suggestions]);

	return (
		<div className="bizF-relative">
			<input
				id="abn"
				ref={inputRef}
				aria-label="ABN"
				type="text"
				value={value}
				onChange={onInputChange}
				className={clsx(
					error
						? "bizF-ring-red-500 focus:bizF-ring-red-500"
						: "bizF-ring-gray-300 focus:bizF-ring-[--bizF-theme-color]",
					"bizF-inset-0 bizF-block bizF-w-full bizF-rounded-md bizF-shadow-sm bizF-border-0 bizF-py-2.5 bizF-px-2.5 bizF-text-gray-900 bizF-ring-1 bizF-outline-1 bizF-outline-gray-900 bizF-ring-inset focus:bizF-ring-1 focus:bizF-ring-inset focus-visible:bizF-outline-[--bizF-theme-color] placeholder:bizF-text-gray-400 sm:bizF-text-md sm:bizF-leading-6 bizF-bg-none"
				)}
				placeholder="ABN*"
			/>
			{error && (
				<span className="bizF-text-red-500 bizF-text-sm">
					{"This field is required"}
				</span>
			)}
			{isLoading && (
				<div className="bizF-text-gray-500 bizF-text-sm">
					Loading suggestions...
				</div>
			)}
			{suggestions.length > 0 && (
				<ul
					className="bizF-absolute bizF-z-50 bizF-bg-white bizF-border bizF-border-gray-300 bizF-rounded-md bizF-shadow-md bizF-max-h-60 bizF-overflow-auto"
					style={dropdownStyle}
				>
					{suggestions.map((suggestion) => (
						<li
							key={suggestion.abn}
							className="bizF-py-2 bizF-pl-3 bizF-cursor-pointer hover:bizF-bg-gray-200"
							onClick={() =>
								handleSuggestionClick(suggestion.abn)
							}
						>
							{suggestion.entityName}
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

export default ABNInput;
